/**
 * Partner Debts API Module
 * Công nợ cộng tác viên — bọc toàn bộ endpoint `/partner-debts/*`.
 * Backend: app/api/partner_debts.py.
 *
 * CHỈ role `secretary` (Thư ký) gọi được. Admin và kế toán đều nhận 403 — đây là
 * quyết định nghiệp vụ của chủ doanh nghiệp (công nợ CTV tách hẳn khỏi mảng kế toán
 * khách hàng/NCC), KHÔNG phải thiếu sót. Đừng "sửa" bằng cách nới quyền cho admin.
 *
 * Tên field trong payload cố ý để nguyên TIẾNG VIỆT không dấu (so_tien, ghi_chu,
 * gach_no…) — đó là đúng hợp đồng backend, đổi sang tiếng Anh ở FE chỉ đẻ thêm một
 * lớp map nữa để sai.
 */

import apiClient, { extractData } from '../api-client';

export const partnerDebtApi = {
  /**
   * Tổng công nợ toàn hệ thống + số dư từng CTV.
   *
   * @param {Object} [params]
   * @param {boolean} [params.chi_con_no] - true (mặc định của backend) = chỉ CTV còn nợ
   * @returns {Promise<Object>} { tong_cong_no, danh_sach: [{ partner_id, ten, domain, so_du }] }
   */
  getOverview(params) {
    return apiClient.get('/partner-debts', { params }).then(extractData);
  },

  /**
   * Chi tiết một CTV: danh sách đơn đã ghi nợ + sao kê sổ nợ.
   *
   * `don_hang[]`: { order_id, order_code, partner_order_code, ngay_vao_sx, trang_thai_don, gia_tri, da_hoan }
   * `so_du` âm = CTV trả trước.
   * `sao_ke[].loai`: DEBIT (nợ tăng) | CREDIT (hoàn nợ) | PAYMENT (thu tiền)
   * `sao_ke[].huy_dong_id`: dòng này là bút toán huỷ của dòng nào
   * `sao_ke[].huy_boi_dong_id`: dòng này đã bị huỷ bởi dòng nào
   * `sao_ke[].huy_duoc`: được hiện nút Huỷ (phiếu thu / giảm nợ ghi tay, chưa huỷ)
   *
   * @param {number} partnerId
   * @returns {Promise<Object>} { partner_id, ten, so_du, don_hang, sao_ke }
   */
  getDetail(partnerId) {
    return apiClient.get(`/partner-debts/${partnerId}`).then(extractData);
  },

  /**
   * Ghi nhận CTV trả tiền, trừ thẳng vào TỔNG nợ (không gắn đơn).
   *
   * Backend trả 400 nếu số tiền vượt tổng nợ hiện tại (hoặc CTV không còn nợ).
   *
   * @param {number} partnerId
   * @param {Object} data
   * @param {number} data.so_tien - Bắt buộc, > 0 và ≤ tổng nợ hiện tại
   * @param {string} [data.ghi_chu]
   * @returns {Promise<Object>} { payment_id, so_tien, so_du_sau_thu }
   */
  createPayment(partnerId, data) {
    return apiClient
      .post(`/partner-debts/${partnerId}/payments`, data)
      .then(extractData);
  },

  /**
   * Giảm TỔNG nợ của CTV có lý do (thay nút hoàn nợ theo đơn — chốt 2026-10-11).
   *
   * Backend chặn vượt nợ hiện tại và bắt buộc lý do. Dòng sổ là CREDIT không gắn đơn,
   * diễn giải "Giảm nợ: <lý do>".
   *
   * @param {number} partnerId
   * @param {Object} data - { so_tien, ly_do }
   * @returns {Promise<Object>} { entry_id, so_tien, so_du_sau_giam }
   */
  createCredit(partnerId, data) {
    return apiClient
      .post(`/partner-debts/${partnerId}/credits`, data)
      .then(extractData);
  },

  /**
   * Huỷ phiếu thu / giảm nợ ghi nhầm — CHỈ super_admin (thư ký nhận 403).
   *
   * Sổ vẫn chỉ ghi thêm: backend ghi một dòng DEBIT đối ứng (`huy_dong_id` trỏ về
   * dòng bị huỷ) và báo site CTV như mọi dòng mới.
   *
   * @param {number} partnerId
   * @param {number} entryId - dòng có `huy_duoc` = true trong sao kê
   * @param {string} lyDo - bắt buộc
   * @returns {Promise<Object>} { entry_id, so_du }
   */
  voidEntry(partnerId, entryId, lyDo) {
    return apiClient
      .post(`/partner-debts/${partnerId}/entries/${entryId}/void`, { ly_do: lyDo })
      .then(extractData);
  },
};
